<template>
  <RightSheet style="width: 90vw;" v-model="dialogVisible" max-width="1920">
    <div class="dialog-wrapper">
      <!-- 标题 -->
      <div class="dialog-title">
        <v-btn icon variant="text" class="back-btn" @click="closeDialog">
          <v-icon>mdi-reply-outline</v-icon>
          返回
        </v-btn>
        <span v-if="clusterData?.name">{{ clusterData?.name }}</span>
        <span v-else>{{ clusterData?.code || '1-1-1#eBlock' }} - 电池簇</span>
        <span class="sample-time">采样时间：{{ currentTime }}</span>
        <div class="title-right">
          <v-btn icon variant="text" class="action-btn" @click="fetchClusterData">
            <v-icon size="20">mdi-refresh</v-icon>
          </v-btn>

          <v-divider vertical class="mx-2 vertical-divider"></v-divider>

          <v-btn icon variant="text" class="action-btn" :class="{ 'active': viewMode === 'chart' }"
            @click="viewMode = 'chart'">
            <v-icon size="20">mdi-chart-timeline-variant</v-icon>
          </v-btn>
          <v-btn icon variant="text" class="action-btn" :class="{ 'active': viewMode === 'cell' }"
            @click="viewMode = 'cell'">
            <v-icon size="20">mdi-battery-high</v-icon>
          </v-btn>
          <v-btn icon variant="text" class="action-btn" :class="{ 'active': viewMode === 'table' }"
            @click="viewMode = 'table'">
            <v-icon size="20">mdi-table-large</v-icon>
          </v-btn>

          <v-divider vertical class="mx-2 vertical-divider"></v-divider>

          <v-btn icon variant="text" class="action-btn close-btn" @click="closeDialog">
            <v-icon size="20">mdi-close</v-icon>
          </v-btn>
        </div>
      </div>

      <!-- 主内容区域 -->
      <div class="dialog-main">
        <!-- 上部：四个渐变卡片 -->
        <div class="top-cards-row">
          <div class="info-card voltage-card">
            <div class="card-content">
              <div class="value-group">
                <span class="value">{{ detailData.voltage || 0 }}V</span>
                <span class="label">电压</span>
              </div>
              <div class="icon-wrapper voltage-icon">
                <img src="@/assets/images/station-monitor/detail-dialog/voltage-icon.png" alt="电压" />
              </div>
            </div>
          </div>

          <div class="info-card current-card">
            <div class="card-content">
              <div class="value-group">
                <span class="value">{{ detailData.current || 0 }}A</span>
                <span class="label">电流</span>
              </div>
              <div class="icon-wrapper current-icon">
                <img src="@/assets/images/station-monitor/detail-dialog/current-icon.png" alt="电流" />
              </div>
            </div>
          </div>

          <div class="info-card soc-card">
            <div class="card-content">
              <div class="value-group">
                <span class="value">{{ detailData.soc || 0 }}%</span>
                <span class="label">SOC</span>
              </div>
              <div class="icon-wrapper soc-icon">
                <img src="@/assets/images/station-monitor/detail-dialog/soc-icon.png" alt="SOC" />
              </div>
            </div>
          </div>

          <div class="info-card soh-card">
            <div class="card-content">
              <div class="value-group">
                <span class="value">{{ detailData.soh || '--' }}</span>
                <span class="label">SOH</span>
              </div>
              <div class="icon-wrapper soh-icon">
                <img src="@/assets/images/station-monitor/detail-dialog/soh-icon.png" alt="SOH" />
              </div>
            </div>
          </div>
        </div>

        <!-- 下部：动态组件（内容区统一由 flex 约束高度，避免外层/页面异常滚动条） -->
        <div class="dialog-content">
          <component :is="activeComponent" :batteryData="batteryData" />
        </div>
      </div>
    </div>
  </RightSheet>
</template>

<script setup>
import { ref, computed, watch, onUnmounted, nextTick } from 'vue'
import dayjs from 'dayjs'
import { fmtd } from '@/utils/index'
import { ws_req } from '@/ws/wss-request'
import { useStationContext } from '../../composables/useStationContext'
import RightSheet from '@/components/RightSheet/index.vue'
import ClusterDetailChart from './ClusterDetailChart.vue'
import ClusterDetailCell from './ClusterDetailCell.vue'
import ClusterDetailTable from './ClusterDetailTable.vue'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  clusterData: {
    type: Object,
    default: () => ({})
  },
  eblockId: {
    type: [String, Number],
    default: ''
  }
})

const emit = defineEmits(['update:modelValue'])

const dialogVisible = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
})

// 视图模式
const viewMode = ref('chart')

const activeComponent = computed(() => {
  const map = {
    chart: ClusterDetailChart,
    cell: ClusterDetailCell,
    table: ClusterDetailTable
  }
  return map[viewMode.value] || ClusterDetailChart
})

// 使用 composable 获取电站上下文信息
const { stationId: currentStationId } = useStationContext()

const currentTime = ref('--')
const detailData = ref({
  voltage: 0,
  current: 0,
  soc: 0,
  soh: '--'
})

// 电池原始数据
const batteryData = ref({})

// 轮询定时器
let pollingTimer = null

const closeDialog = () => {
  dialogVisible.value = false
  stopPolling()
}

// SOH 评级函数
const appraiseFromSOH = (soh) => {
  if (soh == null || soh === '') return '--'
  const value = Number(soh)
  if (value >= 95) return '极佳'
  if (value >= 90) return '佳'
  if (value >= 80) return '优秀'
  if (value >= 70) return '良好'
  if (value >= 65) return '合格'
  if (value >= 60) return '建议更换'
  return '停止运行'
}

// 数据处理函数
const messageHandle = (msg = {}) => {
  try {
    if (!msg) return

    // 预处理数据，为 Cell 和 Table 视图准备
    if (msg.bmu && Array.isArray(msg.bmu.bmuList)) {
      const packSize = msg.bmu.packSize || 18 // pack 数量
      const bmuList = msg.bmu.bmuList
      // 一个 pack 的电芯数量
      const cellNumbersInPack = Math.ceil(bmuList.length / packSize)

      // 辅助函数：计算 pack 编号
      const getPackNum = (cellIndex, packSize) => {
        let n = Math.floor(cellIndex / packSize) + 1
        return String(n).padStart(2, '0')
      }

      // 为每个电芯添加编号和 pack 信息
      bmuList.forEach((item, i) => {
        // 如果没有 R 权限，可能需要置0，这里暂不处理权限，直接用数据
        if (item.R == null) item.R = 0

        item.number = i + 1
        item.packNumber = getPackNum(i, cellNumbersInPack)
      })

      // 分组逻辑
      let bumListGroup = []

      for (let i = 0; i < packSize; i++) {
        let start = i * cellNumbersInPack
        let end = Math.min((i + 1) * cellNumbersInPack, bmuList.length)

        bumListGroup.push({
          cells: bmuList.slice(start, end),
          slice: [start, end],
          // packState: 1 // 假设状态
        })
      }

      msg.bmu.bumListGroup = bumListGroup
    }

    batteryData.value = msg

    // 更新采样时间
    currentTime.value = msg.bcs?.Time
      ? dayjs(msg.bcs.Time).format('YYYY-MM-DD HH:mm:ss')
      : '--'

    // 更新顶部卡片数据
    detailData.value = {
      voltage: fmtd(msg.bcs?.TBV, 1, 1) || 0,
      current: fmtd(msg.bcs?.TBC, 1, 1) || 0,
      soc: fmtd(msg.bcs?.SOC, 1, 1) || 0,
      soh: appraiseFromSOH(msg.bcs?.SOH) || '--'
    }

  } catch (error) {
    console.error('[ClusterDetailDialog] 数据处理失败:', error)
  }
}

// 获取电池簇数据
const fetchClusterData = async () => {
  try {
    if (!props.eblockId || !currentStationId.value) {
      console.warn('[ClusterDetailDialog] 缺少 eblockId 或 stationId')
      return
    }

    const result = await ws_req.request('ws_eblock_bmu', {
      eBlockId: props.eblockId,
      stationId: Number(currentStationId.value)
    })

    if (result) {
      messageHandle(result)
    }
  } catch (error) {
    console.error('[ClusterDetailDialog] 获取数据失败:', error)
  }
}

// 开始轮询
const startPolling = () => {
  stopPolling()
  fetchClusterData()
  pollingTimer = setInterval(fetchClusterData, 2000) // 每2秒更新一次
}

// 停止轮询
const stopPolling = () => {
  if (pollingTimer) {
    clearInterval(pollingTimer)
    pollingTimer = null
  }
}

// 监听弹窗打开/关闭
watch(() => dialogVisible.value, (val) => {
  if (val) {
    viewMode.value = 'chart' // 默认打开趋势图
    nextTick(() => {
      // 开始轮询获取数据
      startPolling()
    })
  } else {
    // 关闭时清理图表和定时器
    stopPolling()
  }
})

onUnmounted(() => {
  stopPolling()
})

</script>

<style scoped lang="scss">
.dialog-wrapper {
  position: relative;
  width: 100%;
  height: 100vh;
  background-color: #fff !important;
  background: url('@/assets/images/station-monitor/detail-dialog/detail-bg.svg') no-repeat center center;
  background-size: cover;
  border-radius: 30px 30px 0 0 !important;
  display: flex;
  flex-direction: column;
}

.dialog-title {
  height: 80px;
  display: flex;
  align-items: center;
  padding: 0 40px;
  font-family: 'HarmonyOS Sans SC', sans-serif;
  font-size: 20px;
  color: #2E2E2E;
  position: relative;
}

.back-btn {
  width: 98px;
  height: 40px;
  border-radius: 12px;
  border: 1px solid #C0CFDA;
  margin-right: 20px;
  font-size: 14px;
  color: #2E2E2E;
}

.sample-time {
  font-size: 14px;
  color: rgba(0, 0, 0, 0.6);
  margin-left: auto;
  margin-right: 20px;
}

.title-right {
  display: flex;
  align-items: center;
  // gap: 4px;
}

.action-btn {
  &.active {
    color: #3A68FF;
    background-color: #E6F0FF;
  }
}

.vertical-divider {
  height: 16px;
  border-color: #E0E0E0;
  margin-top: auto;
  margin-bottom: auto;
  align-self: center;
  opacity: 1;
}

.dialog-main {
  height: calc(100vh - 83px);
  background: #F6FBFF;
  border-radius: 30px;
  margin: 0 20px 20px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow-y: auto;
}

/* 顶部四个卡片 */
.top-cards-row {
  display: flex;
  gap: 16px;
  height: 120px;
  width: 100%;
}

.info-card {
  height: 120px;
  flex: 1;
  border-radius: 15px;
  padding: 0 40px;
  display: flex;
  align-items: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  backdrop-filter: blur(2px);

  &.voltage-card {
    background: linear-gradient(167deg, rgba(255, 236, 231, 0.90) -4.35%, rgba(255, 249, 249, 0.60) 73.46%);
  }

  &.current-card {
    background: linear-gradient(202deg, rgba(231, 237, 255, 0.90) 22.69%, rgba(255, 255, 255, 0.60) 172.09%);
  }

  &.soc-card {
    background: linear-gradient(180deg, rgba(231, 255, 245, 0.90) -6.6%, rgba(255, 255, 255, 0.60) 189.4%);
  }

  &.soh-card {
    background: linear-gradient(183deg, rgba(237, 231, 255, 0.90) -1.24%, rgba(255, 255, 255, 0.60) 126.21%);
  }

  .card-content {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .value-group {
    display: flex;
    flex-direction: column;
    // gap: 8px;

    .value {
      font-size: 24px;
      font-weight: 500;
      color: #333;
    }

    .label {
      font-size: 14px;
      color: #666;
      margin-top: 10px;
    }
  }

  .icon-wrapper {
    width: 90px;
    height: 90px;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    margin-top: 10px;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: top center;
    }
  }
}
</style>
